import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Define the language type
type Language = 'en' | 'de';

// Define the context type
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

// Create the context with a default value
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Define the translations
const translations: Record<Language, Record<string, string>> = {
  en: {
    'language.switch': 'Switch to German',
    'language.current': 'en',
    
    // Navigation
    'nav.nearbyListings': 'Nearby Listings',
    'nav.nearby': 'Nearby',
    'nav.blindDates': 'Blind Dates',
    'nav.myDinners': 'My Dinners',
    'nav.myVenues': 'My Venues',
    'nav.myEvents': 'My Events',
    'nav.myReservations': 'My Reservations',
    'nav.reservations': 'Reservations',
    'nav.registerVenue': 'Register Venue',
    'nav.signUp': 'Sign Up',
    'nav.signIn': 'Sign In',
    'nav.signOut': 'Sign Out',
    'nav.profile': 'Profile',
    'nav.menu': 'Menu',
    
    // Common
    'common.loading': 'Loading...',
    'common.error': 'An error occurred',
    'common.date': 'Date',
    'common.time': 'Time',
    'common.description': 'Description',
    'common.name': 'Name',
    'common.email': 'Email',
    'common.phone': 'Phone',
    'common.address': 'Address',
    'common.location': 'Location',
    'common.style': 'Style',
    'common.spotsAvailable': 'spots available',
    'common.joinDinner': 'Join Dinner',
    'common.joinGroup': 'Join Group',
    'common.signInToJoin': 'Sign in to join',
    'common.loginRequired': 'You must be logged in',
    'common.dietaryOptions': 'Dietary Options',
    'common.confirmCancel': 'Are you sure you want to cancel?',
    
    // Nearby
    'nearby.title': 'Discover Nearby',
    'nearby.subtitle': 'Find venues, events, and people near you',
    'nearby.searchPlaceholder': 'Search by name...',
    'nearby.searchRadius': 'Search radius: {radius} miles',
    'nearby.kilometer': 'Kilometer',
    'nearby.kilometers': 'Kilometers',
    'nearby.distance': '{distance} miles away',
    'nearby.venues': 'Venues',
    'nearby.events': 'Events',
    'nearby.people': 'People',
    'nearby.viewDetails': 'View Details',
    'nearby.joinEvent': 'Join Event',
    'nearby.blindDate': 'Blind Date',
    'nearby.drinkingNight': 'Drinking Night',
    'nearby.dinner': 'Dinner',
    'nearby.more': 'more',
    'nearby.noVenues': 'No venues found',
    'nearby.noVenuesMessage': 'Try adjusting your search criteria or location to find venues in your area.',
    'nearby.noEvents': 'No events found',
    'nearby.noEventsMessage': 'Try adjusting your search criteria or location to find events in your area.',
    'nearby.noPeople': 'No people found',
    'nearby.noPeopleMessage': 'Try adjusting your search criteria or location to find people in your area.',
    
    // Search
    'search.title': 'Find Your Next Dinner Experience',
    'search.subtitle': 'Discover dinner events hosted by restaurants and fellow food lovers',
    'search.searchPlaceholder': 'Search by restaurant, cuisine, or location...',
    'search.showFilters': 'Show Filters',
    'search.hideFilters': 'Hide Filters',
    'search.cuisine': 'Cuisine',
    'search.priceRange': 'Price Range',
    'search.noResults': 'No dinners found',
    'search.checkBack': 'Check back later for new dinner events or create your own!',
    'search.hostDinner': 'Host a Dinner',
    
    // Blind Dates
    'blindDates.title': 'Blind Dates',
    'blindDates.subtitle': 'Join a blind date dinner and meet new people',
    'blindDates.searchPlaceholder': 'Search blind dates...',
    'blindDates.noResults': 'No blind dates found',
    'blindDates.checkBack': 'Check back later for new blind dates or host your own!',
    'blindDates.join': 'Join Blind Date',
    'blindDates.joining': 'Joining...',
    'blindDates.hostDate': 'Host a Blind Date',
    'blindDates.liveMusic': 'Live Music',
    'blindDates.createTitle': 'Host a Blind Date',
    'blindDates.venueType': 'Venue Type',
    'blindDates.selectRestaurant': 'Select Restaurant',
    'blindDates.selectBar': 'Select Bar',
    'blindDates.selectVenue': 'Select a venue',
    'blindDates.selectVenueError': 'Please select a venue',
    'blindDates.groupSize': 'Group Size',
    'blindDates.numberOfPeople': 'Number of people',
    'blindDates.selectPriceRange': 'Select price range',
    'blindDates.priceRanges.budget': 'Budget-friendly',
    'blindDates.priceRanges.moderate': 'Moderate',
    'blindDates.priceRanges.upscale': 'Upscale',
    'blindDates.priceRanges.luxury': 'Luxury',
    'blindDates.ageRange': 'Age Range',
    'blindDates.selectAgeRange': 'Select age range',
    'blindDates.ageRanges.over56': '56+',
    'blindDates.genderPreference': 'Gender Preference',
    'blindDates.selectGenderPreference': 'Select gender preference',
    'blindDates.genderPreferences.any': 'Any',
    'blindDates.genderPreferences.male': 'Male',
    'blindDates.genderPreferences.female': 'Female',
    'blindDates.genderPreferences.mixed': 'Mixed Group',
    'blindDates.diningStyle': 'Dining Style',
    'blindDates.selectDiningStyle': 'Select dining style',
    'blindDates.diningStyles.casual': 'Casual',
    'blindDates.diningStyles.formal': 'Formal',
    'blindDates.diningStyles.business': 'Business',
    'blindDates.diningStyles.romantic': 'Romantic',
    'blindDates.diningStyles.social': 'Social',
    'blindDates.descriptionPlaceholder': 'Tell potential participants about the experience...',
    'blindDates.creating': 'Creating Blind Date...',
    'blindDates.createButton': 'Create Blind Date',
    'blindDates.createError': 'Failed to create blind date',
    'blindDates.fetchVenuesError': 'Failed to fetch venues',
    'blindDates.noVenuesFound': 'No approved venues found',
    'blindDates.music': 'Music',
    'blindDates.atmosphere': 'Atmosphere',
    
    // Drinking Buddies
    'drinkingBuddies.title': 'Drinking Buddies',
    'drinkingBuddies.subtitle': 'Find companions for a night out at your favorite bars',
    'drinkingBuddies.noResults': 'No drinking nights found',
    'drinkingBuddies.checkBack': 'Check back later for new drinking nights or host your own!',
    'drinkingBuddies.hostBarNight': 'Host a Bar Night',
    
    // Last Minute
    'lastMinute.title': 'Last Minute Dinners',
    'lastMinute.subtitle': 'Find dinner events happening today or tomorrow',
    'lastMinute.searchPlaceholder': 'Search last minute dinners...',
    'lastMinute.noResults': 'No last minute dinners found',
    'lastMinute.checkBack': 'Check back later for new last minute dinners!',
    'lastMinute.joining': 'Joining...',
    'lastMinute.distance': '{distance} miles away',
    
    // Overseas
    'overseas.title': 'Overseas Adventures',
    'overseas.subtitle': 'Connect with food lovers around the world',
    'overseas.chooseLocation': 'Choose a Location',
    'overseas.enterLocation': 'Enter a location to find food lovers around the world',
    'overseas.noResults': 'No profiles found in this location',
    'overseas.tryAdjusting': 'Try adjusting your search criteria or choosing a different location',
    
    // Discover
    'discover.title': 'Discover People',
    'discover.subtitle': 'Find food lovers with similar interests',
    'discover.languages': 'Languages',
    'discover.interests': 'Interests',
    'discover.dietary': 'Dietary',
    'discover.message': 'Message',
    'discover.noResults': 'No profiles found',
    'discover.tryAdjusting': 'Try adjusting your search criteria or location',
    
    // Profile
    'profile.about': 'About Me',
    'profile.activityStats': 'Activity Stats',
    'profile.dinnersAttended': 'Dinners Attended',
    'profile.friends': 'Friends',
    'profile.blindDates': 'Blind Dates',
    'profile.daysActive': 'Days Active',
    'profile.achievements': 'Achievements',
    'profile.diningPreferences': 'Dining Preferences',
    'profile.favoriteCuisines': 'Favorite Cuisines',
    'profile.dietaryPreferences': 'Dietary Preferences',
    'profile.priceRange': 'Price Range',
    'profile.preferredDiningTimes': 'Preferred Dining Times',
    'profile.drinkingPreference': 'Drinking Preference',
    'profile.smokingPreference': 'Smoking Preference',
    'profile.languages': 'Languages',
    'profile.interests': 'Interests',
    'profile.referFriends': 'Invite Restaurants',
    'profile.pendingRequests': 'Pending Requests',
    'profile.recommendations': 'Dinner Recommendations',
    'profile.noPendingRequests': 'No pending friend requests',
    'profile.acceptRequest': 'Accept Request',
    'profile.rejectRequest': 'Reject Request',
    'profile.changePicture': 'Change profile picture',
    'profile.pictureError': 'Error uploading profile picture',
    'profile.notFound': 'Profile Not Found',
    'profile.notFoundMessage': 'The profile you are looking for does not exist or has been removed.',
    'profile.goHome': 'Go to Home Page',
    
    // My Dinners
    'myDinners.title': 'My Dinners',
    'myDinners.subtitle': 'Manage your dinner events and participations',
    'myDinners.upcoming': 'Upcoming',
    'myDinners.past': 'Past',
    'myDinners.recommendations': 'Recommendations',
    'myDinners.noUpcoming': 'No upcoming dinners',
    'myDinners.noPast': 'No past dinners',
    'myDinners.upcomingMessage': 'You have no upcoming dinners. Join a dinner or create your own!',
    'myDinners.pastMessage': 'You have not attended any dinners yet.',
    'myDinners.findDinners': 'Find Dinners',
    'myDinners.getDirections': 'Get Directions',
    'myDinners.with': 'With',
    'myDinners.dinner': 'Dinner',
    'myDinners.blindDate': 'Blind Date',
    'myDinners.drinkingNight': 'Drinking Night',
    'myDinners.dinnerWithFriend': 'Dinner with Friend',
    'myDinners.noRecommendations': 'No dinner recommendations',
    'myDinners.recommendationsMessage': 'You have no dinner recommendations. Invite friends to dinner!',
    'myDinners.recommendedByMe': 'Recommended by me',
    'myDinners.acceptedFromOthers': 'Accepted from others',
    'myDinners.recommendedOn': 'Recommended on',
    'myDinners.dinnerInvitation': 'Dinner Invitation',
    
    // My Events
    'myEvents.title': 'My Events',
    'myEvents.subtitle': 'Manage your hosted events at',
    'myEvents.createDinner': 'Create Dinner',
    'myEvents.createBarNight': 'Create Bar Night',
    'myEvents.hostBlindDate': 'Host Blind Date',
    'myEvents.noEvents': 'No Events Yet',
    'myEvents.noEventsMessage': 'You haven\'t created any events yet. Create your first event!',
    'myEvents.eventType.dinner': 'Dinner',
    'myEvents.eventType.blindDate': 'Blind Date',
    'myEvents.eventType.barNight': 'Bar Night',
    'myEvents.spots': 'Spots:',
    'myEvents.edit': 'Edit',
    'myEvents.cancel': 'Cancel',
    'myEvents.status.open': 'Open',
    'myEvents.status.full': 'Full',
    'myEvents.status.cancelled': 'Cancelled',
    'myEvents.status.completed': 'Completed',
    
    // Venue
    'venue.register.title': 'Register Your Venue',
    'venue.register.restaurant': 'Restaurant',
    'venue.register.bar': 'Bar',
    'venue.register.pictures': 'Pictures',
    'venue.register.features': 'Features',
    'venue.register.submit': 'Register Venue',
    
    // Reservations
    'reservations.title': 'Make a Reservation',
    'reservations.subtitle': 'Book a table at your favorite restaurant',
    'reservations.searchPlaceholder': 'Search restaurants...',
    'reservations.noResults': 'No restaurants found',
    'reservations.noResultsMessage': 'Try adjusting your search criteria or location',
    'reservations.makeReservation': 'Make a Reservation',
    'reservations.partySize': 'Party Size',
    'reservations.person': 'Person',
    'reservations.people': 'People',
    'reservations.specialRequests': 'Special Requests',
    'reservations.specialRequestsPlaceholder': 'Any special requests or dietary requirements...',
    'reservations.completeReservation': 'Complete Reservation',
    'reservations.selectTime': 'Select Time',
    'reservations.outdoorSeating': 'Outdoor Seating Available',
    
    // Home
    'home.hero.title': 'Connect Through Culinary Experiences',
    'home.hero.subtitle': 'Find dinner companions, explore new restaurants, and share memorable meals',
    'home.hero.cta': 'Explore Nearby',
    'home.stats.weeklyUsers': 'Active Users',
    'home.stats.weeklyRestaurants': 'Partner Restaurants',
    'home.stats.weeklyDinners': 'Dinners Hosted',
    'home.features.title': 'How Meet&Eat Works',
    'home.features.subtitle': 'Our platform makes it easy to connect with others over great food',
    'home.features.search.title': 'Discover',
    'home.features.search.description': 'Find restaurants, events, and people near you with similar dining interests',
    'home.features.meet.title': 'Connect',
    'home.features.meet.description': 'Join dinner events or create your own to meet new people',
    'home.features.share.title': 'Experience',
    'home.features.share.description': 'Share memorable dining experiences and build lasting connections',
    'home.philosophy.title': 'Our Philosophy',
    'home.philosophy.description': 'We believe that sharing a meal is one of the most powerful ways to connect with others. Our mission is to bring people together through food.',
    'home.philosophy.cta': 'Learn More',
    'home.impact.title': 'Our Impact',
    'home.impact.subtitle': 'See how Meet&Eat is changing the way people connect',
    'home.impact.connections': 'Connections Made',
    'home.impact.rating': 'User Rating',
    'home.impact.cities': 'Cities',
    'home.impact.onTime': 'On-Time Arrivals',
    'home.howItWorks.title': 'Simple as 1-2-3',
    'home.howItWorks.subtitle': 'Getting started with Meet&Eat is easy',
    'home.howItWorks.step1.title': 'Create Your Profile',
    'home.howItWorks.step1.description': 'Sign up and tell us about your dining preferences and interests',
    'home.howItWorks.step2.title': 'Find or Host Events',
    'home.howItWorks.step2.description': 'Discover dinner events near you or create your own',
    'home.howItWorks.step3.title': 'Enjoy the Experience',
    'home.howItWorks.step3.description': 'Meet new people and enjoy great food together',
    'home.categories.title': 'Explore Categories',
    'home.categories.subtitle': 'Discover different ways to connect with others',
    'home.categories.blindDates': 'Blind Dates',
    'home.categories.groups': 'Group Dinners',
    'home.categories.fineDining': 'Fine Dining',
    'home.testimonials.title': 'What Our Users Say',
    'home.testimonials.subtitle': 'Hear from people who have found connections through Meet&Eat',
    'home.testimonials.sarah.name': 'Sarah K.',
    'home.testimonials.sarah.text': 'I\'ve met some of my closest friends through Meet&Eat. The blind date dinners are my favorite!',
    'home.testimonials.sarah.location': 'Vienna, Austria',
    'home.testimonials.michael.name': 'Michael T.',
    'home.testimonials.michael.text': 'As someone new to the city, this app has been a lifesaver for meeting people and discovering great restaurants.',
    'home.testimonials.michael.location': 'Graz, Austria',
    'home.testimonials.emma.name': 'Emma L.',
    'home.testimonials.emma.text': 'The quality of connections I\'ve made through Meet&Eat is amazing. It\'s not just about the food, but the people!',
    'home.testimonials.emma.location': 'Salzburg, Austria',
    'home.growth.title': 'Growing Community',
    'home.growth.subtitle': 'Join thousands of food lovers connecting every day',
    'home.growth.userGrowth': 'User Growth',
    'home.growth.satisfactionRate': 'Satisfaction Rate',
    'home.growth.newCities': 'New Cities This Year',
    'home.cta.title': 'Ready to Join?',
    'home.cta.subtitle': 'Create your profile today and start connecting with food lovers near you',
    'home.cta.button': 'Sign Up Now',
    
    // FAQ
    'faq.title': 'Frequently Asked Questions',
    'faq.howItWorks.question': 'How does Meet&Eat work?',
    'faq.howItWorks.answer': 'Meet&Eat connects food lovers for shared dining experiences. You can join existing dinner events, create your own, or participate in blind date dinners. Simply create a profile, browse events near you, and start connecting!',
    'faq.isFree.question': 'Is Meet&Eat free to use?',
    'faq.isFree.answer': 'Yes, the basic features of Meet&Eat are free to use. We offer premium features for enhanced experiences, but you can join dinners, create events, and connect with others at no cost.',
    'faq.joinDinner.question': 'How do I join a dinner?',
    'faq.joinDinner.answer': 'Browse available dinners in your area, select one that interests you, and click "Join Dinner." You\'ll receive confirmation and details about the event, including the restaurant location and time.',
    'faq.blindDates.question': 'What are Blind Date dinners?',
    'faq.blindDates.answer': 'Blind Date dinners are group dining experiences where you don\'t know who else will be attending until you arrive. It\'s a fun way to meet new people without the pressure of one-on-one dating.',
    'faq.createEvent.question': 'Can I create my own dinner event?',
    'faq.createEvent.answer': 'Absolutely! You can create your own dinner event by selecting "Create Dinner" from the menu. Choose a restaurant, set the date and time, specify how many people you\'d like to join you, and add any preferences or requirements.',
    'faq.privacy.question': 'How does Meet&Eat protect my privacy?',
    'faq.privacy.answer': 'We take privacy seriously. You control what information is visible on your profile, and you can adjust your privacy settings at any time. We never share your personal contact information with other users without your consent.',
    'faq.cancellation.question': 'What if I need to cancel my participation?',
    'faq.cancellation.answer': 'You can cancel your participation in an event up to 24 hours before the scheduled time without any penalty. For cancellations less than 24 hours before the event, please contact the host directly.',
    'faq.becomePartner.question': 'I own a restaurant. How can I become a partner?',
    'faq.becomePartner.answer': 'We\'d love to have you as a partner! Register your restaurant through the "Register Venue" option in the menu. Once approved, you can host events, offer special promotions, and connect with our community of food lovers.',
    'faq.drinkingNights.question': 'What are Drinking Nights?',
    'faq.drinkingNights.answer': 'Drinking Nights are social gatherings at bars rather than restaurants. They focus more on socializing over drinks rather than a full dining experience. You can find or create these events in the Drinking Buddies section.',
    'faq.safety.question': 'How does Meet&Eat ensure user safety?',
    'faq.safety.answer': 'Safety is our priority. We verify all users and venues, implement a rating system, and provide reporting tools. We recommend meeting in public places, informing someone about your plans, and using common sense when meeting new people.',
  },
  de: {
    'language.switch': 'Zu Englisch wechseln',
    'language.current': 'de',
    
    // Navigation
    'nav.nearbyListings': 'Anfragen in der Nähe',
    'nav.nearby': 'In der Nähe',
    'nav.blindDates': 'Blind Dates',
    'nav.myDinners': 'Meine Dinner',
    'nav.myVenues': 'Meine Lokale',
    'nav.myEvents': 'Meine Events',
    'nav.myReservations': 'Meine Reservierungen',
    'nav.reservations': 'Reservierungen',
    'nav.registerVenue': 'Lokal registrieren',
    'nav.signUp': 'Registrieren',
    'nav.signIn': 'Anmelden',
    'nav.signOut': 'Abmelden',
    'nav.profile': 'Profil',
    'nav.menu': 'Menü',
    
    // Common
    'common.loading': 'Lädt...',
    'common.error': 'Ein Fehler ist aufgetreten',
    'common.date': 'Datum',
    'common.time': 'Uhrzeit',
    'common.description': 'Beschreibung',
    'common.name': 'Name',
    'common.email': 'E-Mail',
    'common.phone': 'Telefon',
    'common.address': 'Adresse',
    'common.location': 'Standort',
    'common.style': 'Stil',
    'common.spotsAvailable': 'Plätze verfügbar',
    'common.joinDinner': 'Am Dinner teilnehmen',
    'common.joinGroup': 'Gruppe beitreten',
    'common.signInToJoin': 'Anmelden zum Beitreten',
    'common.loginRequired': 'Anmeldung erforderlich',
    'common.dietaryOptions': 'Ernährungsoptionen',
    'common.confirmCancel': 'Möchtest du wirklich abbrechen?',
    
    // Nearby
    'nearby.title': 'Entdecke in der Nähe',
    'nearby.subtitle': 'Finde Lokale, Events und Leute in deiner Umgebung',
    'nearby.searchPlaceholder': 'Nach Namen suchen...',
    'nearby.searchRadius': 'Suchradius: {radius} Kilometer',
    'nearby.kilometer': 'Kilometer',
    'nearby.kilometers': 'Kilometer',
    'nearby.distance': '{distance} Kilometer entfernt',
    'nearby.venues': 'Lokale',
    'nearby.events': 'Events',
    'nearby.people': 'Personen',
    'nearby.viewDetails': 'Details ansehen',
    'nearby.joinEvent': 'Event beitreten',
    'nearby.blindDate': 'Blind Date',
    'nearby.drinkingNight': 'Trinkabend',
    'nearby.dinner': 'Dinner',
    'nearby.more': 'weitere',
    'nearby.noVenues': 'Keine Lokale gefunden',
    'nearby.noVenuesMessage': 'Versuche deine Suchkriterien oder deinen Standort anzupassen, um Lokale in deiner Nähe zu finden.',
    'nearby.noEvents': 'Keine Events gefunden',
    'nearby.noEventsMessage': 'Versuche deine Suchkriterien oder deinen Standort anzupassen, um Events in deiner Nähe zu finden.',
    'nearby.noPeople': 'Keine Personen gefunden',
    'nearby.noPeopleMessage': 'Versuche deine Suchkriterien oder deinen Standort anzupassen, um Personen in deiner Nähe zu finden.',
    
    // Search
    'search.title': 'Finde dein nächstes Dinner-Erlebnis',
    'search.subtitle': 'Entdecke Dinner-Events von Restaurants und anderen Feinschmeckern',
    'search.searchPlaceholder': 'Suche nach Restaurant, Küche oder Ort...',
    'search.showFilters': 'Filter anzeigen',
    'search.hideFilters': 'Filter ausblenden',
    'search.cuisine': 'Küche',
    'search.priceRange': 'Preisklasse',
    'search.noResults': 'Keine Dinner gefunden',
    'search.checkBack': 'Schau später wieder vorbei oder erstelle dein eigenes Dinner!',
    'search.hostDinner': 'Dinner veranstalten',
    
    // Blind Dates
    'blindDates.title': 'Blind Dates',
    'blindDates.subtitle': 'Nimm an einem Blind Date Dinner teil und lerne neue Leute kennen',
    'blindDates.searchPlaceholder': 'Blind Dates durchsuchen...',
    'blindDates.noResults': 'Keine Blind Dates gefunden',
    'blindDates.checkBack': 'Schau später wieder vorbei oder veranstalte dein eigenes Blind Date!',
    'blindDates.join': 'Am Blind Date teilnehmen',
    'blindDates.joining': 'Trete bei...',
    'blindDates.hostDate': 'Blind Date veranstalten',
    'blindDates.liveMusic': 'Live-Musik',
    'blindDates.createTitle': 'Blind Date veranstalten',
    'blindDates.venueType': 'Lokaltyp',
    'blindDates.selectRestaurant': 'Restaurant auswählen',
    'blindDates.selectBar': 'Bar auswählen',
    'blindDates.selectVenue': 'Lokal auswählen',
    'blindDates.selectVenueError': 'Bitte wähle ein Lokal aus',
    'blindDates.groupSize': 'Gruppengröße',
    'blindDates.numberOfPeople': 'Anzahl der Personen',
    'blindDates.selectPriceRange': 'Preisklasse wählen',
    'blindDates.priceRanges.budget': 'Günstig',
    'blindDates.priceRanges.moderate': 'Moderat',
    'blindDates.priceRanges.upscale': 'Gehoben',
    'blindDates.priceRanges.luxury': 'Luxuriös',
    'blindDates.ageRange': 'Altersgruppe',
    'blindDates.selectAgeRange': 'Altersgruppe wählen',
    'blindDates.ageRanges.over56': '56+',
    'blindDates.genderPreference': 'Geschlechterpräferenz',
    'blindDates.selectGenderPreference': 'Geschlechterpräferenz wählen',
    'blindDates.genderPreferences.any': 'Alle',
    'blindDates.genderPreferences.male': 'Männlich',
    'blindDates.genderPreferences.female': 'Weiblich',
    'blindDates.genderPreferences.mixed': 'Gemischt',
    'blindDates.diningStyle': 'Dinner-Stil',
    'blindDates.selectDiningStyle': 'Dinner-Stil wählen',
    'blindDates.diningStyles.casual': 'Casual',
    'blindDates.diningStyles.formal': 'Formal',
    'blindDates.diningStyles.business': 'Business',
    'blindDates.diningStyles.romantic': 'Romantisch',
    'blindDates.diningStyles.social': 'Social',
    'blindDates.descriptionPlaceholder': 'Erzähle potenziellen Teilnehmern von dem Erlebnis...',
    'blindDates.creating': 'Erstelle Blind Date...',
    'blindDates.createButton': 'Blind Date erstellen',
    'blindDates.createError': 'Fehler beim Erstellen des Blind Dates',
    'blindDates.fetchVenuesError': 'Fehler beim Laden der Lokale',
    'blindDates.noVenuesFound': 'Keine freigegebenen Lokale gefunden',
    'blindDates.music': 'Musik',
    'blindDates.atmosphere': 'Atmosphäre',
    
    // Drinking Buddies
    'drinkingBuddies.title': 'Trinkpartner',
    'drinkingBuddies.subtitle': 'Finde Begleitung für einen Abend in deiner Lieblingsbar',
    'drinkingBuddies.noResults': 'Keine Trinkabende gefunden',
    'drinkingBuddies.checkBack': 'Schau später wieder vorbei oder veranstalte deinen eigenen Trinkabend!',
    'drinkingBuddies.hostBarNight': 'Barabend veranstalten',
    
    // Last Minute
    'lastMinute.title': 'Last Minute Dinner',
    'lastMinute.subtitle': 'Finde Dinner-Events, die heute oder morgen stattfinden',
    'lastMinute.searchPlaceholder': 'Last Minute Dinner durchsuchen...',
    'lastMinute.noResults': 'Keine Last Minute Dinner gefunden',
    'lastMinute.checkBack': 'Schau später wieder vorbei für neue Last Minute Angebote!',
    'lastMinute.joining': 'Trete bei...',
    'lastMinute.distance': '{distance} Kilometer entfernt',
    
    // Overseas
    'overseas.title': 'Auslandsabenteuer',
    'overseas.subtitle': 'Verbinde dich mit Feinschmeckern auf der ganzen Welt',
    'overseas.chooseLocation': 'Wähle einen Ort',
    'overseas.enterLocation': 'Gib einen Ort ein, um Feinschmecker auf der ganzen Welt zu finden',
    'overseas.noResults': 'Keine Profile an diesem Ort gefunden',
    'overseas.tryAdjusting': 'Versuche, deine Suchkriterien anzupassen oder einen anderen Ort zu wählen',
    
    // Discover
    'discover.title': 'Entdecke Menschen',
    'discover.subtitle': 'Finde Feinschmecker mit ähnlichen Interessen',
    'discover.languages': 'Sprachen',
    'discover.interests': 'Interessen',
    'discover.dietary': 'Ernährung',
    'discover.message': 'Nachricht',
    'discover.noResults': 'Keine Profile gefunden',
    'discover.tryAdjusting': 'Versuche, deine Suchkriterien oder deinen Standort anzupassen',
    
    // Profile
    'profile.about': 'Über mich',
    'profile.activityStats': 'Aktivitätsstatistiken',
    'profile.dinnersAttended': 'Besuchte Dinner',
    'profile.friends': 'Freunde',
    'profile.blindDates': 'Blind Dates',
    'profile.daysActive': 'Tage aktiv',
    'profile.achievements': 'Erfolge',
    'profile.diningPreferences': 'Essensvorlieben',
    'profile.favoriteCuisines': 'Lieblingsküchen',
    'profile.dietaryPreferences': 'Ernährungsvorlieben',
    'profile.priceRange': 'Preisklasse',
    'profile.preferredDiningTimes': 'Bevorzugte Essenszeiten',
    'profile.drinkingPreference': 'Trinkgewohnheiten',
    'profile.smokingPreference': 'Rauchgewohnheiten',
    'profile.languages': 'Sprachen',
    'profile.interests': 'Interessen',
    'profile.referFriends': 'Restaurants einladen',
    'profile.pendingRequests': 'Ausstehende Anfragen',
    'profile.recommendations': 'Dinner-Empfehlungen',
    'profile.noPendingRequests': 'Keine ausstehenden Freundschaftsanfragen',
    'profile.acceptRequest': 'Anfrage annehmen',
    'profile.rejectRequest': 'Anfrage ablehnen',
    'profile.changePicture': 'Profilbild ändern',
    'profile.pictureError': 'Fehler beim Hochladen des Profilbilds',
    'profile.notFound': 'Profil nicht gefunden',
    'profile.notFoundMessage': 'Das gesuchte Profil existiert nicht oder wurde entfernt.',
    'profile.goHome': 'Zur Startseite',
    
    // My Dinners
    'myDinners.title': 'Meine Dinner',
    'myDinners.subtitle': 'Verwalte deine Dinner-Events und Teilnahmen',
    'myDinners.upcoming': 'Bevorstehend',
    'myDinners.past': 'Vergangen',
    'myDinners.recommendations': 'Empfehlungen',
    'myDinners.noUpcoming': 'Keine bevorstehenden Dinner',
    'myDinners.noPast': 'Keine vergangenen Dinner',
    'myDinners.upcomingMessage': 'Du hast keine bevorstehenden Dinner. Nimm an einem Dinner teil oder erstelle dein eigenes!',
    'myDinners.pastMessage': 'Du hast noch an keinen Dinnern teilgenommen.',
    'myDinners.findDinners': 'Dinner finden',
    'myDinners.getDirections': 'Route anzeigen',
    'myDinners.with': 'Mit',
    'myDinners.dinner': 'Dinner',
    'myDinners.blindDate': 'Blind Date',
    'myDinners.drinkingNight': 'Trinkabend',
    'myDinners.dinnerWithFriend': 'Dinner mit Freund',
    'myDinners.noRecommendations': 'Keine Dinner-Empfehlungen',
    'myDinners.recommendationsMessage': 'Du hast keine Dinner-Empfehlungen. Lade Freunde zum Dinner ein!',
    'myDinners.recommendedByMe': 'Von mir empfohlen',
    'myDinners.acceptedFromOthers': 'Von anderen angenommen',
    'myDinners.recommendedOn': 'Empfohlen am',
    'myDinners.dinnerInvitation': 'Dinner-Einladung',
    
    // My Events
    'myEvents.title': 'Meine Events',
    'myEvents.subtitle': 'Verwalte deine veranstalteten Events bei',
    'myEvents.createDinner': 'Dinner erstellen',
    'myEvents.createBarNight': 'Barabend erstellen',
    'myEvents.hostBlindDate': 'Blind Date veranstalten',
    'myEvents.noEvents': 'Noch keine Events',
    'myEvents.noEventsMessage': 'Du hast noch keine Events erstellt. Erstelle dein erstes Event!',
    'myEvents.eventType.dinner': 'Dinner',
    'myEvents.eventType.blindDate': 'Blind Date',
    'myEvents.eventType.barNight': 'Barabend',
    'myEvents.spots': 'Plätze:',
    'myEvents.edit': 'Bearbeiten',
    'myEvents.cancel': 'Abbrechen',
    'myEvents.status.open': 'Offen',
    'myEvents.status.full': 'Voll',
    'myEvents.status.cancelled': 'Abgesagt',
    'myEvents.status.completed': 'Abgeschlossen',
    
    // Venue
    'venue.register.title': 'Registriere dein Lokal',
    'venue.register.restaurant': 'Restaurant',
    'venue.register.bar': 'Bar',
    'venue.register.pictures': 'Bilder',
    'venue.register.features': 'Ausstattung',
    'venue.register.submit': 'Lokal registrieren',
    
    // Reservations
    'reservations.title': 'Reservierung vornehmen',
    'reservations.subtitle': 'Buche einen Tisch in deinem Lieblingsrestaurant',
    'reservations.searchPlaceholder': 'Restaurants durchsuchen...',
    'reservations.noResults': 'Keine Restaurants gefunden',
    'reservations.noResultsMessage': 'Versuche, deine Suchkriterien oder deinen Standort anzupassen',
    'reservations.makeReservation': 'Reservierung vornehmen',
    'reservations.partySize': 'Gruppengröße',
    'reservations.person': 'Person',
    'reservations.people': 'Personen',
    'reservations.specialRequests': 'Besondere Wünsche',
    'reservations.specialRequestsPlaceholder': 'Besondere Wünsche oder Ernährungsanforderungen...',
    'reservations.completeReservation': 'Reservierung abschließen',
    'reservations.selectTime': 'Uhrzeit wählen',
    'reservations.outdoorSeating': 'Außenbereich verfügbar',
    
    // Home
    'home.hero.title': 'Verbinde dich durch kulinarische Erlebnisse',
    'home.hero.subtitle': 'Finde Dinner-Begleiter, entdecke neue Restaurants und teile unvergessliche Mahlzeiten',
    'home.hero.cta': 'In der Nähe erkunden',
    'home.stats.weeklyUsers': 'Aktive Nutzer',
    'home.stats.weeklyRestaurants': 'Partnerrestaurants',
    'home.stats.weeklyDinners': 'Veranstaltete Dinner',
    'home.features.title': 'So funktioniert Meet&Eat',
    'home.features.subtitle': 'Unsere Plattform macht es einfach, sich mit anderen bei gutem Essen zu verbinden',
    'home.features.search.title': 'Entdecken',
    'home.features.search.description': 'Finde Restaurants, Events und Menschen in deiner Nähe mit ähnlichen Essensinteressen',
    'home.features.meet.title': 'Verbinden',
    'home.features.meet.description': 'Nimm an Dinner-Events teil oder erstelle deine eigenen, um neue Leute kennenzulernen',
    'home.features.share.title': 'Erleben',
    'home.features.share.description': 'Teile unvergessliche Essenserlebnisse und knüpfe dauerhafte Verbindungen',
    'home.philosophy.title': 'Unsere Philosophie',
    'home.philosophy.description': 'Wir glauben, dass das Teilen einer Mahlzeit eine der wirkungsvollsten Möglichkeiten ist, sich mit anderen zu verbinden. Unsere Mission ist es, Menschen durch Essen zusammenzubringen.',
    'home.philosophy.cta': 'Mehr erfahren',
    'home.impact.title': 'Unsere Wirkung',
    'home.impact.subtitle': 'Sieh, wie Meet&Eat die Art und Weise verändert, wie Menschen sich verbinden',
    'home.impact.connections': 'Hergestellte Verbindungen',
    'home.impact.rating': 'Nutzerbewertung',
    'home.impact.cities': 'Städte',
    'home.impact.onTime': 'Pünktliche Ankünfte',
    'home.howItWorks.title': 'Einfach wie 1-2-3',
    'home.howItWorks.subtitle': 'Der Einstieg bei Meet&Eat ist einfach',
    'home.howItWorks.step1.title': 'Erstelle dein Profil',
    'home.howItWorks.step1.description': 'Melde dich an und erzähle uns von deinen Essensvorlieben und Interessen',
    'home.howItWorks.step2.title': 'Finde oder veranstalte Events',
    'home.howItWorks.step2.description': 'Entdecke Dinner-Events in deiner Nähe oder erstelle deine eigenen',
    'home.howItWorks.step3.title': 'Genieße das Erlebnis',
    'home.howItWorks.step3.description': 'Lerne neue Leute kennen und genieße gemeinsam gutes Essen',
    'home.categories.title': 'Kategorien erkunden',
    'home.categories.subtitle': 'Entdecke verschiedene Möglichkeiten, dich mit anderen zu verbinden',
    'home.categories.blindDates': 'Blind Dates',
    'home.categories.groups': 'Gruppenessen',
    'home.categories.fineDining': 'Gehobene Küche',
    'home.testimonials.title': 'Was unsere Nutzer sagen',
    'home.testimonials.subtitle': 'Höre von Menschen, die durch Meet&Eat Verbindungen gefunden haben',
    'home.testimonials.sarah.name': 'Sarah K.',
    'home.testimonials.sarah.text': 'Ich habe einige meiner engsten Freunde durch Meet&Eat kennengelernt. Die Blind Date Dinner sind meine Favoriten!',
    'home.testimonials.sarah.location': 'Wien, Österreich',
    'home.testimonials.michael.name': 'Michael T.',
    'home.testimonials.michael.text': 'Als jemand, der neu in der Stadt ist, war diese App ein Lebensretter, um Leute kennenzulernen und tolle Restaurants zu entdecken.',
    'home.testimonials.michael.location': 'Graz, Österreich',
    'home.testimonials.emma.name': 'Emma L.',
    'home.testimonials.emma.text': 'Die Qualität der Verbindungen, die ich durch Meet&Eat geknüpft habe, ist erstaunlich. Es geht nicht nur ums Essen, sondern um die Menschen!',
    'home.testimonials.emma.location': 'Salzburg, Österreich',
    'home.growth.title': 'Wachsende Gemeinschaft',
    'home.growth.subtitle': 'Schließe dich Tausenden von Feinschmeckern an, die sich täglich verbinden',
    'home.growth.userGrowth': 'Nutzerwachstum',
    'home.growth.satisfactionRate': 'Zufriedenheitsrate',
    'home.growth.newCities': 'Neue Städte dieses Jahr',
    'home.cta.title': 'Bereit zum Mitmachen?',
    'home.cta.subtitle': 'Erstelle noch heute dein Profil und beginne, dich mit Feinschmeckern in deiner Nähe zu verbinden',
    'home.cta.button': 'Jetzt anmelden',
    
    // FAQ
    'faq.title': 'Häufig gestellte Fragen',
    'faq.howItWorks.question': 'Wie funktioniert Meet&Eat?',
    'faq.howItWorks.answer': 'Meet&Eat verbindet Feinschmecker für gemeinsame Essenserlebnisse. Du kannst an bestehenden Dinner-Events teilnehmen, deine eigenen erstellen oder an Blind Date Dinnern teilnehmen. Erstelle einfach ein Profil, durchsuche Events in deiner Nähe und beginne, dich zu vernetzen!',
    'faq.isFree.question': 'Ist Meet&Eat kostenlos?',
    'faq.isFree.answer': 'Ja, die Grundfunktionen von Meet&Eat sind kostenlos. Wir bieten Premium-Funktionen für erweiterte Erlebnisse an, aber du kannst kostenlos an Dinnern teilnehmen, Events erstellen und dich mit anderen verbinden.',
    'faq.joinDinner.question': 'Wie nehme ich an einem Dinner teil?',
    'faq.joinDinner.answer': 'Durchsuche verfügbare Dinner in deiner Umgebung, wähle eines aus, das dich interessiert, und klicke auf "Am Dinner teilnehmen". Du erhältst eine Bestätigung und Details zum Event, einschließlich Restaurantstandort und Uhrzeit.',
    'faq.blindDates.question': 'Was sind Blind Date Dinner?',
    'faq.blindDates.answer': 'Blind Date Dinner sind Gruppen-Essenserlebnisse, bei denen du nicht weißt, wer sonst noch teilnimmt, bis du ankommst. Es ist eine unterhaltsame Möglichkeit, neue Leute kennenzulernen, ohne den Druck eines Einzeldates.',
    'faq.createEvent.question': 'Kann ich mein eigenes Dinner-Event erstellen?',
    'faq.createEvent.answer': 'Auf jeden Fall! Du kannst dein eigenes Dinner-Event erstellen, indem du "Dinner erstellen" aus dem Menü auswählst. Wähle ein Restaurant, lege Datum und Uhrzeit fest, gib an, wie viele Personen du dabei haben möchtest, und füge Präferenzen oder Anforderungen hinzu.',
    'faq.privacy.question': 'Wie schützt Meet&Eat meine Privatsphäre?',
    'faq.privacy.answer': 'Wir nehmen Privatsphäre ernst. Du kontrollierst, welche Informationen in deinem Profil sichtbar sind, und kannst deine Datenschutzeinstellungen jederzeit anpassen. Wir geben deine persönlichen Kontaktinformationen niemals ohne deine Zustimmung an andere Benutzer weiter.',
    'faq.cancellation.question': 'Was, wenn ich meine Teilnahme absagen muss?',
    'faq.cancellation.answer': 'Du kannst deine Teilnahme an einem Event bis zu 24 Stunden vor dem geplanten Zeitpunkt ohne Strafe absagen. Bei Absagen weniger als 24 Stunden vor dem Event kontaktiere bitte direkt den Gastgeber.',
    'faq.becomePartner.question': 'Ich besitze ein Restaurant. Wie kann ich Partner werden?',
    'faq.becomePartner.answer': 'Wir würden uns freuen, dich als Partner zu haben! Registriere dein Restaurant über die Option "Lokal registrieren" im Menü. Nach der Genehmigung kannst du Events veranstalten, Sonderangebote anbieten und dich mit unserer Gemeinschaft von Feinschmeckern verbinden.',
    'faq.drinkingNights.question': 'Was sind Trinkabende?',
    'faq.drinkingNights.answer': 'Trinkabende sind gesellige Zusammenkünfte in Bars statt in Restaurants. Sie konzentrieren sich mehr auf das gesellige Beisammensein bei Getränken als auf ein vollständiges Essenserlebnis. Du kannst diese Events im Bereich Trinkpartner finden oder erstellen.',
    'faq.safety.question': 'Wie gewährleistet Meet&Eat die Sicherheit der Benutzer?',
    'faq.safety.answer': 'Sicherheit hat für uns Priorität. Wir überprüfen alle Benutzer und Veranstaltungsorte, implementieren ein Bewertungssystem und stellen Meldetools zur Verfügung. Wir empfehlen, sich an öffentlichen Orten zu treffen, jemanden über deine Pläne zu informieren und gesunden Menschenverstand zu verwenden, wenn du neue Leute triffst.',
  }
};

// Create the provider component
interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Get the initial language from localStorage or default to 'en'
  const [language, setLanguage] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem('language');
    return (savedLanguage === 'en' || savedLanguage === 'de') ? savedLanguage : 'en';
  });

  // Update localStorage when language changes
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  // Translation function
  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use the language context
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};